import { Box, Card, CardHeader, Chip, Dialog, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import { UnidadNegocioViewDTO, UnidadNegocioViewDTOFields } from './types'
import { HttpUnidadNegocio } from './api'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { useState } from 'react'
import EditIcon from '@mui/icons-material/Edit'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import EditUnidadNegocio from './EditUnidadNegocio'
import { ItecColFilters, ItecDataGrid, ItecGridColsDef as GridColsDef, useItecDataGrid } from 'src/lib/itaptec/datagrid/src'
import CachedIcon from '@mui/icons-material/Cached'
import { FilterAltOff, Search } from '@mui/icons-material'
import { WrapperTable } from '../components'
import { CountryAvatar } from '../../../../../components/icons/CountryAvatar'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { EntityWithIdAndDescriptionFields, EntityWithIdFields, PaisLogoFields, schemaSelectionOption } from 'src/types'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { Controller } from 'react-hook-form'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { Button } from 'src/components/forms'
import { ICON_MAS } from '../../../../sistema/components/SharedIcons'

const schemaFiltro = z.object({
    nombre: z.string().optional(),
    codsPais: z.array(schemaSelectionOption).optional()
})

type SchemaFiltro = z.infer<typeof schemaFiltro>

function TableUnidadesNegocio() {
    const { askConfirmation } = useConfirmationDialog()
    const { paisLogos } = usePaisLogo()
    const [selectedItem, setSelectedItem] = useState<UnidadNegocioViewDTO>()
    const [showDialog, setShowDialog] = useState(false)

    const columnas: GridColsDef<UnidadNegocioViewDTO>[] = [
        {
            headerName: 'Nombre',
            field: UnidadNegocioViewDTOFields.Nombre,
            flex: 2
        },
        {
            headerName: 'País',
            field: UnidadNegocioViewDTOFields.NombrePais,
            flex: 1,
            renderCell: ({ row }) =>
                row[UnidadNegocioViewDTOFields.NombrePais] ? (
                    <Chip
                        label={row[UnidadNegocioViewDTOFields.NombrePais]}
                        size='small'
                        variant='outlined'
                        avatar={<CountryAvatar countryCode={row[UnidadNegocioViewDTOFields.CodPais]!} />}
                    />
                ) : null
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: ({ row }) => [
                <GridActionsCellItem
                    icon={
                        <Tooltip title='Editar'>
                            <EditIcon color='info' />
                        </Tooltip>
                    }
                    label='Editar'
                    showInMenu
                    onClick={() => {
                        setSelectedItem(row)
                        setShowDialog(true)
                    }}
                />
            ]
        }
    ]

    const filtros: ItecColFilters<SchemaFiltro> = {
        [UnidadNegocioViewDTOFields.Nombre]: {
            type: 'text',
            name: 'nombre',
            props: { debounceTime: 500, size: 'small' }
        },
        [UnidadNegocioViewDTOFields.NombrePais]: {
            type: 'multiselect',
            name: 'codsPais',
            props: { getOptions: () => Promise.resolve([]), size: 'small' }
        },
    }

    const {
        datagridProps,
        control: {
            query: { refetch },
            form: filterForm
        }
    } = useItecDataGrid<UnidadNegocioViewDTO, SchemaFiltro>({
        pagination: false,
        columns: columnas,
        filters: filtros,
        getEntities: (filter) =>
            HttpUnidadNegocio.getAll().then(data => {
                if (!filter) return data
                let filtered = data

                const nombre = filter.nombre
                if (nombre) {
                    const lower = nombre.toLowerCase()
                    filtered = filtered.filter(s =>
                        s[UnidadNegocioViewDTOFields.Nombre]?.toLowerCase().includes(lower)
                    )
                }

                const codsPais = filter.codsPais
                if (codsPais && codsPais.length > 0) {
                    const paisValues = codsPais.map(p => (p as any).id ?? p.value)
                    filtered = filtered.filter(s =>
                        s[UnidadNegocioViewDTOFields.CodPais] !== null &&
                        paisValues.includes(s[UnidadNegocioViewDTOFields.CodPais]!)
                    )
                }

                return filtered
            }),
        density: 'compact'
    })

    const cleanDatagridProps = {
        ...datagridProps,
        columns: datagridProps.columns.map(col => ({
            ...col,
            disableColumnMenu: true,
            renderHeader: undefined
        })),
        slots: undefined
    }

    const handleClearFilters = () => {
        if (!filterForm) return
        filterForm.reset({
            nombre: undefined,
            codsPais: undefined
        })
    }

    return (
        <WrapperTable>
            <Card>
                <CardHeader
                    title='Administración de unidades de negocio'
                    action={
                        <Stack direction='row'>
                            <IconButton disabled={datagridProps.loading} onClick={() => refetch()}>
                                <CachedIcon />
                            </IconButton>
                            <Button
                                variant='contained'
                                color='primary'
                                endIcon={ICON_MAS}
                                onClick={() => {
                                    setSelectedItem(undefined)
                                    setShowDialog(true)
                                }}
                            >
                                Nuevo
                            </Button>
                        </Stack>
                    }
                />

                {filterForm && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'center',
                            mx: 2,
                            mb: 2,
                            p: 2,
                            borderRadius: 1,
                            bgcolor: 'action.hover'
                        }}
                    >
                        <Controller
                            control={filterForm.control}
                            name='nombre'
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    value={field.value ?? ''}
                                    label='Nombre'
                                    size='small'
                                    sx={{ minWidth: 180, flex: 1 }}
                                    InputProps={{
                                        startAdornment: <Search fontSize='small' sx={{ mr: 0.5, color: 'text.secondary' }} />
                                    }}
                                />
                            )}
                        />
                        <Box sx={{ minWidth: 180, flex: 1 }}>
                            <ControlledAutocomplete
                                control={filterForm.control}
                                name='codsPais'
                                label='País'
                                                                loadOptions={() =>
                                    Promise.resolve(paisLogos).then(res =>
                                        res.map(s => ({
                                            id: s.id,
                                            descripcion: s[EntityWithIdAndDescriptionFields.Description]
                                        }))
                                    )
                                }
                                multiple
                            />
                        </Box>
                        <Tooltip title='Limpiar filtros'>
                            <IconButton size='small' onClick={handleClearFilters}>
                                <FilterAltOff fontSize='small' />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                <ItecDataGrid {...cleanDatagridProps} />
            </Card>
            <Dialog open={showDialog} fullWidth maxWidth='sm' keepMounted={false}>
                <EditUnidadNegocio
                    unidadNegocio={selectedItem}
                    onClose={() => {
                        setShowDialog(false)
                        setSelectedItem(undefined)
                        refetch()
                    }}
                />
            </Dialog>
        </WrapperTable>
    )
}

export default TableUnidadesNegocio
